import type { FinanceTransaction } from '@/types/finance';
import { distributeCostCentersWithCharges } from '@/lib/allocations';
import type { AllocationBasis, ChannelLine, ChannelResultReport } from './types';

const UNASSIGNED_KEY = '__sem_canal__';
const UNASSIGNED_COLOR = '#94A3B8';

interface ChannelMeta {
  id: string;
  name: string;
  color: string;
}

export interface ComputeChannelResultInput {
  transactions: FinanceTransaction[];
  /** Centros de custo cadastrados (cada um é um canal). */
  channels: ChannelMeta[];
  from: Date;
  to: Date;
  /** Critério de rateio dos custos fixos. Default: 'revenue' (% da receita). */
  allocationBasis?: AllocationBasis;
}

function inRange(date: Date, from: Date, to: Date): boolean {
  return date.getTime() >= from.getTime() && date.getTime() <= to.getTime();
}

/** Valor efetivo de uma transação: principal + juros + multa. */
function effectiveAmount(tx: FinanceTransaction): number {
  return tx.amount + (tx.interest ?? 0) + (tx.fine ?? 0);
}

function pct(part: number, whole: number): number | null {
  if (whole === 0) return null;
  return (part / whole) * 100;
}

interface Acc {
  revenue: number;
  directCost: number;
  salesCount: number;
}

/**
 * Calcula o resultado por canal (centro de custo) no período.
 *
 * Considera apenas lançamentos com status 'completed' (regime de caixa), usando
 * paymentDate quando disponível, senão dueDate — mesma convenção da DRE.
 *
 * Transferências são ignoradas. Receita sem centro de custo vira o canal
 * "Sem canal". Despesa sem centro de custo entra no pool de custos fixos
 * compartilhados, rateado entre os canais conforme `allocationBasis`.
 *
 * Transação com split de centro de custo (costCenterSplits) tem cada parcela
 * somada no seu canal, com juros/multa distribuídos proporcionalmente.
 */
export function computeChannelResult(input: ComputeChannelResultInput): ChannelResultReport {
  const { transactions, channels, from, to } = input;
  const allocationBasis: AllocationBasis = input.allocationBasis ?? 'revenue';

  const metaById = new Map<string, ChannelMeta>(channels.map((c) => [c.id, c]));
  const acc = new Map<string, Acc>();
  let fixedCostPool = 0;

  const getAcc = (key: string): Acc => {
    let a = acc.get(key);
    if (!a) {
      a = { revenue: 0, directCost: 0, salesCount: 0 };
      acc.set(key, a);
    }
    return a;
  };

  for (const tx of transactions) {
    if (tx.status !== 'completed') continue;
    if (tx.type === 'transfer') continue;
    const refDate = tx.paymentDate ?? tx.dueDate;
    if (!inRange(refDate, from, to)) continue;

    const value = effectiveAmount(tx);
    // Com split de centro de custo, cada parcela (com encargos proporcionais)
    // vai para o seu canal; sem split, 1 parcela virtual com o valor cheio.
    const parts = distributeCostCentersWithCharges(tx);

    if (tx.type === 'income') {
      if (parts.length === 0) {
        const a = getAcc(UNASSIGNED_KEY);
        a.revenue += value;
        a.salesCount += 1;
      } else {
        // salesCount +1 em cada canal participante ("quantas vendas envolvem este canal").
        for (const p of parts) {
          const a = getAcc(p.costCenterId);
          a.revenue += p.amount;
          a.salesCount += 1;
        }
      }
    } else {
      // expense
      if (parts.length === 0) {
        fixedCostPool += value;
      } else {
        for (const p of parts) {
          getAcc(p.costCenterId).directCost += p.amount;
        }
      }
    }
  }

  const totalRevenue = Array.from(acc.values()).reduce((s, a) => s + a.revenue, 0);
  const totalDirectCost = Array.from(acc.values()).reduce((s, a) => s + a.directCost, 0);

  // Rateio dos custos fixos: por % da receita de cada canal sobre a receita total.
  // Caso degenerado (receita total = 0): não há base para ratear → fica não alocado.
  const canAllocate = allocationBasis === 'revenue' && totalRevenue > 0;

  const channelLines: ChannelLine[] = Array.from(acc.entries())
    .filter(([, a]) => a.revenue !== 0 || a.directCost !== 0)
    .map(([key, a]) => {
      const meta = key === UNASSIGNED_KEY ? null : metaById.get(key);
      const contributionMargin = a.revenue - a.directCost;
      const allocatedFixedCost = canAllocate ? fixedCostPool * (a.revenue / totalRevenue) : 0;
      const netProfit = contributionMargin - allocatedFixedCost;
      return {
        channelId: key === UNASSIGNED_KEY ? null : key,
        channelName: meta?.name ?? (key === UNASSIGNED_KEY ? 'Sem canal' : 'Canal removido'),
        color: meta?.color ?? UNASSIGNED_COLOR,
        revenue: a.revenue,
        directCost: a.directCost,
        contributionMargin,
        contributionMarginPct: pct(contributionMargin, a.revenue),
        allocatedFixedCost,
        netProfit,
        netProfitPct: pct(netProfit, a.revenue),
        salesCount: a.salesCount,
      };
    })
    .sort((x, y) => {
      // "Sem canal" sempre por último; demais por receita desc.
      if (x.channelId === null) return 1;
      if (y.channelId === null) return -1;
      return y.revenue - x.revenue;
    });

  const totalContributionMargin = totalRevenue - totalDirectCost;
  const totalNetProfit = totalContributionMargin - fixedCostPool;
  const unassigned = acc.get(UNASSIGNED_KEY);

  return {
    channels: channelLines,
    totalRevenue,
    totalDirectCost,
    totalContributionMargin,
    totalFixedCost: fixedCostPool,
    totalNetProfit,
    hasUnassignedRevenue: Boolean(unassigned && unassigned.revenue > 0),
    unallocatedFixedCost: canAllocate ? 0 : fixedCostPool,
  };
}
